import { Component, Input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule, NgFor } from '@angular/common';
import { CardComponent } from '../../card/card.component';
import { ButtonComponent } from '../../button/button.component';
import { StudentXpSummary } from '../../../../core/interfaces/gamification';

/** Rôle de l'utilisateur, tel que le realm Keycloak le nomme. */
export type UserKind = 'student' | 'professor' | 'supervisor';

@Component({
  selector: 'app-profile-card',
  standalone: true,
  imports: [
    TranslateModule,
    NgFor,
    ButtonComponent,
    CardComponent,
    CommonModule,
  ],
  templateUrl: './profile-card.component.html',
  styleUrl: './profile-card.component.css',
})
export class ProfileCardComponent {
  //statistics = STATIC_BOXES;
  @Input() avatar: string = 'assets/images/default-user.png';
  @Input() name: string = 'User Name';

  /**
   * Rôle affiché sous le nom.
   *
   * <p>Un bandeau d'identité séparé portait cette information au-dessus du
   * tableau de bord : il répétait le nom et l'avatar déjà présents ici, et
   * volait une hauteur d'écran pour une ligne de texte. Le rôle et le parcours
   * l'ont rejoint ; le bandeau a disparu.</p>
   *
   * <p><c>null</c> n'affiche rien — la carte sert aussi hors tableau de bord,
   * où le rôle du lecteur n'est pas en question.</p>
   */
  @Input() kind: UserKind | null = null;

  /** Parcours de l'élève. Ignoré pour les autres rôles. */
  @Input() learningPath: string | null = null;

  get kindLabelKey(): string {
    switch (this.kind) {
      case 'professor':  return 'IDENTITY.PROFESSOR';
      case 'supervisor': return 'IDENTITY.SUPERVISOR';
      default:           return 'IDENTITY.STUDENT';
    }
  }

  get kindIcon(): string {
    switch (this.kind) {
      case 'professor':  return 'fa-solid fa-chalkboard-user';
      case 'supervisor': return 'fa-solid fa-user-shield';
      default:           return 'fa-solid fa-user-graduate';
    }
  }

  /** Le parcours n'a de sens que pour un élève. */
  get showLearningPath(): boolean {
    return this.kind === 'student' && !!this.learningPath?.trim();
  }

  /**
   * Résumé XP de l'élève. <c>null</c> n'affiche rien : la carte sert aussi aux
   * enseignants et aux superviseurs, qui n'accumulent pas d'XP.
   */
  @Input() xp: StudentXpSummary | null = null;

  get showXp(): boolean {
    return this.kind === 'student' && this.xp != null;
  }

  /**
   * Avancement dans le niveau courant, en pourcentage.
   *
   * <p>Le palier vaut <c>xpInCurrentLevel + xpToNextLevel</c> : le backend ne
   * publie pas sa taille, seulement les deux moitiés. Un palier nul — niveau
   * maximal atteint — vaut 100 % plutôt qu'une division par zéro.</p>
   */
  get xpProgress(): number {
    if (!this.xp) return 0;
    const span = this.xp.xpInCurrentLevel + this.xp.xpToNextLevel;
    if (span <= 0) return 100;
    return Math.min(100, Math.round((this.xp.xpInCurrentLevel / span) * 100));
  }
  @Input() points: number | string = 0;
  @Input() pointsSuffix: string = '';
  @Input() statistics: any[] = [];
  @Input() buttonText: string = '';
  @Input() buttonLink: string = '';
  @Input() showButton: boolean = false;
  @Input() customClasses: string = '';
}
